import React, { useState, useRef, useEffect } from "react";
import LoadingBar from "react-top-loading-bar";
import TopBar from "../../sections/TopBar";
import Sidebar from "../../sections/Sidebar";
import "../../customCss/ManageOfficers.css";
import DataService from "../../services/data.service";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";
import NotFound from "../../images/no-data-found.jpeg";
import Spinner from "../../sections/Spinner";
import {
    TextField,
    Box,
    Button,
    Grid
} from "@mui/material";
import ContactFormPopup from "./ContactFormPopup";
import moment from "moment";

const ContactFormEntries = () => {
    const ref = useRef();
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [allEntries, setAllEntries] = useState([]);
    const [filteredEntries, setFilteredEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openForm, setOpenForm] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [deletePopup, setDeletePopup] = useState(false);
    const [btnLoading, setBtnLoading] = useState(false);
    const [viewMode, setViewMode] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        document.title = "Contact Form Submissions || Admin Panel";
        window.scrollTo(0, 0);
        getAllEntries();

        const handleResize = () => {
            if (window.innerWidth < 768) setIsSidebarOpen(false);
        };
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

    const getAllEntries = () => {
        ref.current.continuousStart();
        setLoading(true);
        DataService.getContactForm()
            .then((res) => {
                const data = res?.data?.data || [];
                setAllEntries(data.reverse());
                setFilteredEntries(data);
                ref.current.complete();
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                toast.error("Failed to fetch submissions");
                ref.current.complete();
                setLoading(false);
            });
    };

    const handleSearch = (e) => {
        const query = e.target.value.toLowerCase();
        setSearchQuery(query);
        if (query) {
            const result = allEntries.filter((item) =>
                item.name?.toLowerCase().includes(query) ||
                item.businessName?.toLowerCase().includes(query) ||
                item.email?.toLowerCase().includes(query)
            );
            setFilteredEntries(result);
        } else {
            setFilteredEntries(allEntries);
        }
    };

    const handleAdd = () => {
        setSelectedEntry(null);
        setViewMode(false);
        setOpenForm(true);
    };

    const handleEdit = (entry) => {
        setSelectedEntry(entry);
        setViewMode(false);
        setOpenForm(true);
    };

    const handleView = (entry) => {
        setSelectedEntry(entry);
        setViewMode(true);
        setOpenForm(true);
    };

    const handleDelete = () => {
        if (!selectedEntry) return;
        setBtnLoading(true);
        DataService.deleteContactForm(selectedEntry._id)
            .then(() => {
                toast.success("Submission deleted successfully");
                setDeletePopup(false);
                getAllEntries();
            })
            .catch((err) => {
                const msg = err.response?.data?.message || "Failed to delete submission";
                toast.error(msg);
            })
            .finally(() => {
                setBtnLoading(false);
            });
    };

    const handleFormSubmit = (formData) => {
        setBtnLoading(true);
        if (selectedEntry) {
            DataService.updateContactForm(selectedEntry._id, formData)
                .then(() => {
                    toast.success("Submission updated successfully");
                    setOpenForm(false);
                    getAllEntries();
                })
                .catch((err) => {
                    const msg = err.response?.data?.message || "Failed to update submission";
                    toast.error(msg);
                })
                .finally(() => setBtnLoading(false));
        } else {
            DataService.createContactFormEntry(formData)
                .then(() => {
                    toast.success("Submission added successfully");
                    setOpenForm(false);
                    getAllEntries();
                })
                .catch((err) => {
                    const msg = err.response?.data?.message || "Failed to add submission";
                    toast.error(msg);
                })
                .finally(() => setBtnLoading(false));
        }
    };

    return (
        <>
            <LoadingBar color="#084e8a" ref={ref} height={5} />
            <div className={`main_layout ${isSidebarOpen ? "sidebar-open" : "sidebar-closed"}`}>
                <Sidebar isSidebarOpen={isSidebarOpen} />
                <div className="innerRight">
                    <TopBar title="Contact Form Submissions" menu="" toggleSidebar={toggleSidebar} />
                    <div className="officersSec">
                        <div className="row align-items-center mb-3">
                            <div className="col-sm-6 col-6">
                                <h3 className="colorHeadThree mb-0">Form Submissions</h3>
                            </div>
                            <div className="col-sm-6 col-6 text-right">
                                <button className="btn btn-primary" onClick={handleAdd}>
                                    <i className="fas fa-plus-circle me-2" />
                                    Add New
                                </button>
                            </div>
                        </div>

                        <Box sx={{ mb: 3, p: 2, bgcolor: "#f8f9fa", borderRadius: "8px" }}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Search Submissions"
                                        size="small"
                                        placeholder="Search by Name, Business, or Email"
                                        sx={{ background: "#fff" }}
                                        value={searchQuery}
                                        onChange={handleSearch}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        {filteredEntries.length > 0 ? (
                            <div className="tableScroller">
                                <table className="table table-striped">
                                    <thead style={{ whiteSpace: "nowrap" }}>
                                        <tr>
                                            <th scope="col">S. No.</th>
                                            <th scope="col">Name</th>
                                            <th scope="col">Business Name</th>
                                            <th scope="col">Email</th>
                                            <th scope="col">Security Type</th>
                                            <th scope="col">Visit Date</th>
                                            <th scope="col">Time</th>
                                            <th scope="col">Status</th>
                                            <th scope="col" className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody style={{ whiteSpace: "nowrap" }}>
                                        {filteredEntries.map((item, i) => (
                                            <tr key={i}>
                                                <td>{i + 1}</td>
                                                <td>{item.name || "-"}</td>
                                                <td>{item.businessName || "-"}</td>
                                                <td>{item.email || "-"}</td>
                                                <td style={{ textTransform: 'capitalize' }}>{item.typeOfSecurity || "-"}</td>
                                                <td>{item.visit_date ? moment(item.visit_date).format("ll") : "-"}</td>
                                                <td>
                                                    {item.timingForSecurity?.split(" - ").map(t =>
                                                        moment(t, "HH:mm").format("LT")
                                                    ).join(" - ") || "-"}
                                                </td>
                                                <td style={{ textTransform: 'capitalize' }}>
                                                    <span className={`badge ${item.status === 'active' ? 'bg-success' : 'bg-secondary'} text-white`}>
                                                        {item.status || "-"}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <div className="tIco">
                                                        <i
                                                            className="fas fa-eye me-3"
                                                            onClick={() => handleView(item)}
                                                            title="View"
                                                            style={{ cursor: 'pointer' }}
                                                        />
                                                        <i
                                                            className="fas fa-pencil-alt me-3"
                                                            onClick={() => handleEdit(item)}
                                                            title="Edit"
                                                            style={{ cursor: 'pointer' }}
                                                        />
                                                        <i
                                                            className="fas fa-trash-alt me-3"
                                                            onClick={() => {
                                                                setSelectedEntry(item);
                                                                setDeletePopup(true);
                                                            }}
                                                            title="Delete"
                                                            style={{ cursor: 'pointer' }}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            !loading && (
                                <div className="text-center mt-4">
                                    <img src={NotFound} alt="noData" className="noData mb-2" />
                                    <p className="m-0">No Records Found.</p>
                                </div>
                            )
                        )}
                    </div>
                </div>
            </div>

            <ContactFormPopup
                open={openForm}
                onClose={() => setOpenForm(false)}
                initialData={selectedEntry}
                onSubmit={handleFormSubmit}
                loading={btnLoading}
                readOnly={viewMode}
            />

            <Modal
                show={deletePopup}
                onHide={() => setDeletePopup(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Delete Submission</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center">
                        <p className="mb-0">
                            Are you sure you want to delete the submission from <strong>{selectedEntry?.name}</strong>?
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleDelete}
                        disabled={btnLoading}
                    >
                        {btnLoading ? <Spinner /> : "Delete"}
                    </button>
                    <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => setDeletePopup(false)}
                    >
                        Cancel
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ContactFormEntries;
